import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, ChevronDown } from "lucide-react";
import toast from "react-hot-toast";

import { CustomButton } from "@/components/ui/Buttons";
import { useAppDispatch, useAppSelector } from "@/redux/hooks/hooks";
import { resetUserChanges, updateUser } from "@/redux/feature/user/userSlice";
import { useUpdateCreatorMutation } from "@/redux/feature/user/userApi";
import { handleApiRequest } from "@/utils/handleApiRequest";

import AuthInput from "@/pages/authenticationV2/signUp/components/AuthInput";
import AuthOptionGroup, {
  AuthOptionItem,
} from "@/pages/authenticationV2/signUp/components/AuthOptionGroup";

import type { FileDetails, TUser } from "@/types/types";
import { WHO_CAN_APPEAR_IN } from "@/constant/applicationFormConstant";

import ProfileLinksSectionProfile from "../ProfileLinksSectionProfile";

/** ---------- assets (from your Step5) ---------- */
import DslrImage from "@/assets/images/auth/recording-cor-setup/dslr.png";
import IphoneImage from "@/assets/images/auth/recording-cor-setup/iphone.png";
import AndroidImage from "@/assets/images/auth/recording-cor-setup/android.png";
import TripodImage from "@/assets/images/auth/recording-cor-setup/tripod.png";
import MicrophoneImage from "@/assets/images/auth/recording-cor-setup/microphone.png";
import NoiseControlImage from "@/assets/images/auth/recording-cor-setup/noise-control-room.png";
import ArtificialLightingImage from "@/assets/images/auth/recording-cor-setup/artificial-light.png";
import AestheticImage from "@/assets/images/auth/recording-cor-setup/aesthetic-bg.png";
import GreenScreenImage from "@/assets/images/auth/recording-cor-setup/gree-screen.png";
import PlusImage from "@/assets/images/auth/recording-cor-setup/plus.png";

/** ---- Types (same as your V2 types) ---- */
export interface EquipmentItemV2 {
  type: string;
  details: string;
}

export type LanguageFluency =
  "basic" | "conversational" | "fluent" | "professional" | "native" | "";

export type LanguageItemV2 = {
  id: string;
  name: string;
  fluency: LanguageFluency;
  isCustom?: boolean;
  createdAt: string;
};

export type PortfolioVideoSourceType = "upload" | "link";

export type TSocialPlatform =
  | "instagram"
  | "linkedin"
  | "tiktok"
  | "behance"
  | "youtube"
  | "facebook"
  | "x"
  | "website"
  | "custom";

export type TProfileLinkItem = {
  id: string;
  platform: TSocialPlatform;
  label?: string;
  url: string;
  followers?: number | null;
};

export type PortfolioVideoItem = {
  id: string;
  brandName: string;
  isPersonalProject: boolean;
  description: string;
  sourceType: PortfolioVideoSourceType;
  linkUrl?: string;
  file?: FileDetails;
  uploading?: boolean;
  uploadError?: string | null;
  createdAt: string;
};

/** ✅ robust empty checker */
const isEmptyValue = (v: unknown): boolean => {
  if (v === null || v === undefined) return true;
  if (typeof v === "string") return v.trim().length === 0;
  if (Array.isArray(v))
    return v.length === 0 || v.every((x) => isEmptyValue(x));
  if (v instanceof Date) return false;
  if (typeof v === "object")
    return Object.keys(v as Record<string, unknown>).length === 0;
  return false;
};

const SectionDivider = ({ title }: { title: string }) => (
  <div className="flex items-center gap-x-2">
    <div className="bg-[#FF6600] px-[8.5px] rounded-[20px] shrink-0">
      <p className="text-white text-[11px] leading-5 tracking-normal">
        {title}
      </p>
    </div>
    <div className="flex-1 h-px bg-[#F2F4F7]" />
  </div>
);

/** ---------- Core Setup (Step5 style) ---------- */
const CORE_SETUP_OPTIONS = [
  { type: "dslr", label: "DSLR Camera", icon: DslrImage },
  { type: "iphone", label: "Iphone", icon: IphoneImage },
  { type: "androidPhone", label: "Android Phone", icon: AndroidImage },
  { type: "tripod", label: "Tripod / Stable mount", icon: TripodImage },
  {
    type: "microphone",
    label: "External microphone",
    icon: MicrophoneImage,
  },
  {
    type: "noise-control",
    label: "Quiet / noise-controlled room",
    icon: NoiseControlImage,
  },
  {
    type: "artificial-lighting",
    label: "Artificial lighting",
    icon: ArtificialLightingImage,
  },
  { type: "green-screen", label: "Green screen", icon: GreenScreenImage },
  {
    type: "aesthetic-bg",
    label: "Aesthetic background",
    icon: AestheticImage,
  },
  { type: "others", label: "Others", icon: PlusImage },
] as const;

function CoreSetupSection({
  value,
  onChange,
}: {
  value: EquipmentItemV2[];
  onChange: (next: EquipmentItemV2[]) => void;
}) {
  const coreSetup = Array.isArray(value) ? value : [];

  const isSelected = (type: string) => coreSetup.some((i) => i.type === type);

  const toggleCore = (type: string) => {
    const exists = isSelected(type);
    const next = exists
      ? coreSetup.filter((i) => i.type !== type)
      : [...coreSetup, { type, details: "" }];
    onChange(next);
  };

  const setDetails = (type: string, details: string) => {
    const next = coreSetup.map((i) =>
      i.type === type ? { ...i, details } : i,
    );
    onChange(next);
  };

  return (
    <div>
      <label className="text-sm tracking-normal mb-2 block">
        <span className="text-[#121217] font-medium">Core Setup</span>{" "}
        <span className="text-[#475467]">(What can you record with?)</span>
      </label>

      {/* Chips */}
      <div className="flex gap-2 flex-wrap mt-2">
        {CORE_SETUP_OPTIONS.map((opt) => {
          const active = isSelected(opt.type);
          return (
            <button
              key={opt.type}
              type="button"
              onClick={() => toggleCore(opt.type)}
              className={`border py-[8.5px] px-[7px] rounded-lg flex items-center gap-x-1
                ${
                  active
                    ? "border-[#FF6600] bg-[#FFF4EC]"
                    : "border-[#D8D8DD] bg-white"
                }`}
            >
              <img src={opt.icon} alt={opt.label} className="size-5" />
              <p className="text-xs tracking-[-0.5px]">{opt.label}</p>
            </button>
          );
        })}
      </div>

      <label className="text-[#475467] text-[10px] tracking-normal">
        Select all that apply
      </label>

      {/* Details for selected */}
      <div className="mt-3 space-y-2.5">
        {coreSetup.map((item) => {
          const opt = CORE_SETUP_OPTIONS.find((o) => o.type === item.type);
          if (!opt) return null;

          return (
            <div
              key={item.type}
              className="flex items-center gap-2 rounded-lg border border-[#F2F4F7] bg-white px-2.5 py-1.5"
            >
              <img
                src={opt.icon}
                alt={opt.label}
                className="size-4 shrink-0 opacity-80"
              />

              <p className="text-[11px] font-medium text-[#475467] whitespace-nowrap">
                {opt.label}
              </p>

              <input
                type="text"
                value={item.details || ""}
                onChange={(e) => setDetails(item.type, e.target.value)}
                placeholder="e.g. Canon D2780"
                className="min-w-0 flex-1 bg-transparent text-[11px] text-[#1D2939] placeholder:text-[#98A2B3] outline-none border-none focus:ring-0"
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** ---------- Avoid topics (Step style chips) ---------- */
const AVOID_TOPICS_PRESETS = ["None", "Alcohol", "Gambling", "Adult products"];

function AvoidTopicsSection({
  value,
  onChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const avoidTopics = Array.isArray(value) ? value : [];

  const toggleAvoidTopic = (topic: string) => {
    if (avoidTopics.includes(topic))
      onChange(avoidTopics.filter((t) => t !== topic));
    else onChange([...avoidTopics, topic]);
  };

  return (
    <div>
      <p className="tracking-normal text-sm">
        <span className="text-[#121217] font-medium">
          Topics you prefer not to work with
        </span>{" "}
        <span className="text-[#475467]">(optional)</span>
      </p>
      <p className="mt-[2px] text-[10px] tracking-normal text-[#475467]">
        This helps us avoid mismatched projects
      </p>

      <div className="flex flex-wrap mt-2 gap-2">
        {AVOID_TOPICS_PRESETS.map((t) => {
          const active =
            t === "None" ? avoidTopics.length === 0 : avoidTopics.includes(t);

          return (
            <button
              key={t}
              type="button"
              onClick={() => {
                if (t === "None") onChange([]);
                else toggleAvoidTopic(t);
              }}
              className={`px-1.5 py-1 border rounded-lg text-xs tracking-[-0.5px]
                ${
                  active
                    ? "border-[#FF6600] bg-[#FFF4EC]"
                    : "border-[#D8D8DD] bg-white"
                }`}
            >
              {t}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** ---------- ComfortableWith (Step style blocks) ---------- */
function ComfortBlock({
  title,
  subDescription,
  options,
  value,
  onChange,
}: {
  title: string;
  subDescription: string;
  options: string[];
  value: string;
  onChange: (val: string) => void;
}) {
  return (
    <div className="border border-[#EDEDED] rounded-xl p-3 bg-white">
      <div>
        <p className="text-xs font-semibold text-[#121217]">{title}</p>
        <p className="text-[10px] text-[#475467] mt-1">{subDescription}</p>
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        {options.map((opt) => {
          const active = value === opt;
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onChange(opt)}
              className={`px-2 py-1 rounded-lg border text-[11px] tracking-[-0.4px]
                ${
                  active
                    ? "border-[#FF6600] bg-[#FFF4EC]"
                    : "border-[#D8D8DD] bg-white"
                }`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ComfortableWithSection({
  value,
  onChange,
}: {
  value: Record<string, any>;
  onChange: (next: Record<string, any>) => void;
}) {
  const comfortableWith =
    value && typeof value === "object" ? (value as Record<string, any>) : {};

  const setComfort = (key: string, val: string) => {
    onChange({ ...comfortableWith, [key]: val });
  };

  return (
    <div>
      <div>
        <h6 className="text-[#121217] text-sm tracking-normal font-medium">
          Creator Add-ons & Permissions
        </h6>
        <p className="text-[#475467] text-[10px] tracking-normal mt-[2px]">
          Some brands may need extra support beyond content creation. Select
          what you’re open to offering.
        </p>
      </div>

      <div className="w-full space-y-2 mt-4">
        <label className="text-xs font-medium text-[#121217] tracking-[-1.5%]">
          What are you comfortable with?
        </label>

        <div className="space-y-3">
          <ComfortBlock
            title="Create a new account for a brand"
            subDescription="For brands that need a fresh account for campaigns."
            options={["Yes, I can set up an account", "Not available"]}
            value={comfortableWith.createBrandAccount || ""}
            onChange={(val) => setComfort("createBrandAccount", val)}
          />

          <ComfortBlock
            title="Post content from your own account"
            subDescription="Includes organic posts or collab posts."
            options={["Yes, I can post from my account", "Not available"]}
            value={comfortableWith.postFromOwnAccount || ""}
            onChange={(val) => setComfort("postFromOwnAccount", val)}
          />

          <ComfortBlock
            title="Run ads via your account (Collab / Branded Ads)"
            subDescription="Only for brand-approved, paid collaborations."
            options={["Yes, I’m open to running ads", "Not available"]}
            value={comfortableWith.runAds || ""}
            onChange={(val) => setComfort("runAds", val)}
          />

          {/* If you want contentFarming later, uncomment:
          <ComfortBlock
            title="Content Farming (Optional Add-on)"
            subDescription="Create multiple pieces of content over time for a brand."
            options={["Yes, open to content farming", "One-off projects only"]}
            value={comfortableWith.contentFarming || ""}
            onChange={(val) => setComfort("contentFarming", val)}
          />
          */}
        </div>
      </div>
    </div>
  );
}

/** ---------- Languages (same UX as your LanguagesSection, but controlled) ---------- */
const FLUENCY_OPTIONS: {
  value: Exclude<LanguageFluency, "">;
  label: string;
}[] = [
  { value: "basic", label: "Basic" },
  { value: "conversational", label: "Conversational" },
  { value: "fluent", label: "Fluent" },
  { value: "professional", label: "Professional" },
  { value: "native", label: "Native" },
];

const PRESET_LANGUAGES = [
  "English",
  "Hindi",
  "Bengali",
  "Marathi",
  "Tamil",
  "Telugu",
  "Gujarati",
  "Kannada",
  "Malayalam",
  "Punjabi",
  "Bhojpuri",
];

const normalize = (v: string) => v.trim().replace(/\s+/g, " ");
const keyify = (v: string) => normalize(v).toLowerCase();

const FluencyDropdown = ({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (val: string) => void;
  placeholder: string;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selected = FLUENCY_OPTIONS.find((o) => o.value === value);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className={`
          flex items-center gap-1.5 bg-white border rounded-full
          text-[11px] px-2.5 py-1 outline-none transition-all duration-200
          ${
            open
              ? "border-[#F97316] shadow-[0_0_0_2px_rgba(249,115,22,0.12)]"
              : "border-[#FDE2D1] hover:border-[#F97316]"
          }
        `}
      >
        <span className={selected ? "text-[#121217]" : "text-[#9CA3AF]"}>
          {selected?.label || placeholder}
        </span>
        <ChevronDown
          size={12}
          strokeWidth={2.5}
          className={`text-[#F97316] transition-transform duration-200 shrink-0 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <div className="absolute z-50 top-full mt-1.5 left-0 min-w-[120px] bg-white rounded-xl border border-[#E5E7EB] shadow-[0_8px_24px_rgba(0,0,0,0.08)] overflow-hidden py-1">
          {FLUENCY_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
              className={`
                w-full flex items-center justify-between px-3 py-1.5
                text-[12px] text-left transition-colors duration-150
                ${
                  value === o.value
                    ? "bg-[#FFF7F0] text-[#F97316]"
                    : "text-[#374151] hover:bg-[#F9FAFB]"
                }
              `}
            >
              <span>{o.label}</span>
              {value === o.value && (
                <span className="w-1.5 h-1.5 rounded-full bg-[#F97316] shrink-0" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export function LanguagesWithFluencySection({
  value,
  onChange,
}: {
  value: LanguageItemV2[];
  onChange: (next: LanguageItemV2[]) => void;
}) {
  const languages = Array.isArray(value) ? value : [];

  const [isOtherOpen, setIsOtherOpen] = useState(false);
  const [otherInput, setOtherInput] = useState("");

  const selectedKeySet = useMemo(() => {
    return new Set(languages.map((l) => keyify(l.name)));
  }, [languages]);

  const addLanguage = (nameRaw: string, isCustom = false) => {
    const name = normalize(nameRaw);
    if (!name) return;

    const k = keyify(name);
    if (selectedKeySet.has(k)) {
      setOtherInput("");
      return;
    }

    const next: LanguageItemV2[] = [
      ...languages,
      {
        id: crypto.randomUUID(),
        name,
        fluency: "",
        isCustom,
        createdAt: new Date().toISOString(),
      },
    ];

    onChange(next);
    setOtherInput("");
  };

  const removeLanguage = (id: string) => {
    onChange(languages.filter((l) => l.id !== id));
  };

  const setFluency = (id: string, fluency: LanguageFluency) => {
    onChange(languages.map((l) => (l.id === id ? { ...l, fluency } : l)));
  };

  const togglePreset = (name: string) => {
    const exists = languages.find((l) => keyify(l.name) === keyify(name));
    if (exists) {
      removeLanguage(exists.id);
      return;
    }
    addLanguage(name, false);
  };

  const canAddOther = normalize(otherInput).length > 0;
  const getFluencyLabel = (idx: number) =>
    idx === 0 ? "Fluency *" : "Fluency (Optional)";

  return (
    <div className="w-full">
      <div className="mb-2">
        <label className="text-sm tracking-normal block">
          <span className="text-[#121217] font-medium">
            Languages with fluency{" "}
          </span>
          <span className="text-[#475467]">(optional)</span>
        </label>
        <p className="text-[10px] text-[#475467] mt-1">
          Pick languages and set fluency for better matching.
        </p>
      </div>

      {languages.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {languages.map((l, idx) => (
            <div
              key={l.id}
              className="flex items-center gap-2 border border-[#FF6600] bg-[#FFF4EC]
                         rounded-[12px] px-2 py-1.5"
            >
              <span className="text-[12px] font-medium text-[#121217]">
                {l.name}
              </span>

              <FluencyDropdown
                value={l.fluency}
                onChange={(val) => setFluency(l.id, val as LanguageFluency)}
                placeholder={getFluencyLabel(idx)}
              />

              <button
                type="button"
                onClick={() => removeLanguage(l.id)}
                className="p-1 rounded-lg hover:bg-black/5"
                aria-label={`Remove ${l.name}`}
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {PRESET_LANGUAGES.filter(
          (name) => !selectedKeySet.has(keyify(name)),
        ).map((name) => (
          <button
            key={name}
            type="button"
            onClick={() => togglePreset(name)}
            className="border px-3 py-2 rounded-xl text-[12px] leading-none select-none border-[#D8D8DD] bg-white"
          >
            {name}
          </button>
        ))}

        <button
          type="button"
          onClick={() => setIsOtherOpen((p) => !p)}
          className={`border px-3 py-2 rounded-xl text-[12px] leading-none select-none flex items-center gap-1
            ${
              isOtherOpen
                ? "border-[#FF6600] bg-[#FFF4EC]"
                : "border-[#D8D8DD] bg-white"
            }`}
        >
          <span className="text-[#FF6600] font-bold">+</span>
          Other
        </button>
      </div>

      {isOtherOpen && (
        <div className="mt-3">
          <div className="flex items-center gap-2">
            <input
              value={otherInput}
              onChange={(e) => setOtherInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (canAddOther) addLanguage(otherInput, true);
                }
              }}
              placeholder="Type a language (e.g. Urdu)"
              className="flex-1 border border-[#D8D8DD] rounded-xl px-3 py-2 text-sm outline-none focus:border-[#FF6600]"
            />

            <button
              type="button"
              onClick={() => addLanguage(otherInput, true)}
              disabled={!canAddOther}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition
                ${
                  canAddOther
                    ? "bg-[#FF6600] text-white hover:opacity-90"
                    : "bg-[#F2F4F7] text-[#98A2B3] cursor-not-allowed"
                }`}
            >
              Add
            </button>
          </div>

          <p className="text-[10px] text-[#667085] mt-1">
            Tip: press <span className="font-medium">Enter</span> to add
            quickly.
          </p>
        </div>
      )}

      {languages.length > 0 && languages[0]?.fluency === "" && (
        <p className="mt-2 text-[10px] text-[#B42318]">
          Please select fluency for your first language.
        </p>
      )}
    </div>
  );
}

/** -------- Modal -------- */
interface Props {
  data: TUser;
  onClose: React.Dispatch<React.SetStateAction<boolean>>;
}

const EditCreatorExtrasModal = ({ data, onClose }: Props) => {
  const dispatch = useAppDispatch();
  const userSliceData: any = useAppSelector((s) => s.user.user);
  const [updateCreator] = useUpdateCreatorMutation();

  /** Who can appear mapping for AuthOptionGroup (multi) */
  const whoCanAppearValue: AuthOptionItem[] = useMemo(() => {
    const selectedKeys: string[] = Array.isArray(userSliceData?.whoCanAppear)
      ? userSliceData.whoCanAppear
      : [];
    const map = new Map(WHO_CAN_APPEAR_IN.map((o: any) => [o.key, o]));
    return selectedKeys
      .map((k) => map.get(k))
      .filter(Boolean) as AuthOptionItem[];
  }, [userSliceData?.whoCanAppear]);

  const handleWhoCanAppearChange = (items: AuthOptionItem[]) => {
    dispatch(
      updateUser({ field: "whoCanAppear", value: items.map((i) => i.key) }),
    );
  };

  const handleSave = async () => {
    if (!data || !data.user_id) {
      toast.error("User information is missing");
      return;
    }

    const cleanedProfileLinks = (userSliceData?.profileLinks ?? []).filter(
      (link: any) =>
        (link.url && String(link.url).trim() !== "") ||
        (link.followers !== null && link.followers !== undefined),
    );

    const payload = {
      id: data.user_id,
      body: {
        coreSetup: userSliceData?.coreSetup ?? [],
        canBringToVideo: userSliceData?.canBringToVideo ?? "",
        whoCanAppear: userSliceData?.whoCanAppear ?? [],
        avoidTopics: userSliceData?.avoidTopics ?? [],
        comfortableWith: userSliceData?.comfortableWith ?? {},
        profileLinks: cleanedProfileLinks,
        languagesWithFluency: userSliceData?.languagesWithFluency ?? [],
      },
    };

    await handleApiRequest({
      payload,
      requestFunction: updateCreator,
      onSuccess: () => {
        toast.success("Updated successfully!");
        onClose(false);
      },
      onError: (err) => {
        const error = err as {
          data?: { errorSource?: { path?: string; message: string }[] };
        };
        if (error?.data?.errorSource?.length) {
          error.data.errorSource.forEach((item) => {
            toast.error(
              item.path ? `${item.path} is ${item.message}` : item.message,
            );
          });
        } else {
          toast.error("Failed to update. Please try again.");
        }
      },
    });
  };

  const handleCancel = () => {
    dispatch(resetUserChanges(data));
    onClose(false);
  };

  return (
    <section className="bg-white rounded-2xl flex flex-col max-h-[90vh]">
      {/* Header */}
      <div className="flex justify-between items-center px-5 py-4 border-b border-[#F2F4F7] shrink-0">
        <div className="flex items-center gap-x-2">
          <div className="bg-[#FF6600] px-[8.5px] rounded-[20px]">
            <p className="text-white text-[11px] leading-5 tracking-normal">
              Edit
            </p>
          </div>
          <h1 className="text-[#060B13] text-xl tracking-[-0.7px] font-medium">
            Creator Setup & Preferences
          </h1>
        </div>

        <button
          onClick={handleCancel}
          className="text-[#98A2B3] hover:text-[#060B13] transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Body */}
      <div className="overflow-y-auto flex-1 px-5 py-5 space-y-6">
        <SectionDivider title="Recording Setup" />

        <CoreSetupSection
          value={(userSliceData?.coreSetup ?? []) as EquipmentItemV2[]}
          onChange={(next) =>
            dispatch(updateUser({ field: "coreSetup", value: next }))
          }
        />

        <AuthOptionGroup
          label="Who can appear in your videos?"
          options={WHO_CAN_APPEAR_IN}
          multiple
          value={whoCanAppearValue}
          onChange={handleWhoCanAppearChange}
        />

        <AuthInput
          label="Tell us what you can bring to your video"
          name="canBringToVideo"
          placeholder="e.g. Professional lighting, acting experience, props, creative ideas..."
          value={userSliceData?.canBringToVideo ?? ""}
          onChange={(e) =>
            dispatch(
              updateUser({ field: "canBringToVideo", value: e.target.value }),
            )
          }
        />

        <SectionDivider title="Preferences" />

        <AvoidTopicsSection
          value={(userSliceData?.avoidTopics ?? []) as string[]}
          onChange={(next) =>
            dispatch(updateUser({ field: "avoidTopics", value: next }))
          }
        />

        <ComfortableWithSection
          value={(userSliceData?.comfortableWith ?? {}) as Record<string, any>}
          onChange={(next) =>
            dispatch(updateUser({ field: "comfortableWith", value: next }))
          }
        />

        <SectionDivider title="Links" />

        <ProfileLinksSectionProfile />
        {/* 
        <LanguagesWithFluencySection
          value={
            (userSliceData?.languagesWithFluency ?? []) as LanguageItemV2[]
          }
          onChange={(next) =>
            dispatch(updateUser({ field: "languagesWithFluency", value: next }))
          }
        /> */}

        {/* small hint */}
        <div className="border border-[#EDEDED] py-[7px] px-2 rounded-xl w-fit">
          <p className="text-[#475467] text-[10px] tracking-normal">
            😉 You can edit this anytime. Only relevant brands see your details.
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-[#F2F4F7] shrink-0">
        <CustomButton
          variant="outline"
          border="1px solid #E4E4E4"
          fontSize="14px"
          fontWeight="600"
          color="#4D4D4D"
          padding="5px 10.5px"
          borderRadius="200px"
          className="tracking-[-0.11px]"
          onClick={handleCancel}
        >
          Cancel
        </CustomButton>

        <CustomButton
          backgroundColor="#FF6600"
          fontSize="14px"
          fontWeight="600"
          color="white"
          padding="5px 10.5px"
          borderRadius="200px"
          className="tracking-[-0.11px]"
          onClick={handleSave}
        >
          Save Changes
        </CustomButton>
      </div>
    </section>
  );
};

export default EditCreatorExtrasModal;
